import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Select, Toast } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { useQuery, useMutation } from "@/hooks/useApi";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { useUrlParams } from "@/hooks/useUrlParams";
import type { TaxonomyMode } from "@/api/submissions.service";
import {
  mailingGroupsService,
  type MailingGroupsSummaryItem,
  type CreateMailingGroupBody,
} from "@/api/mailing-groups.service";
import { ROUTES } from "@/routes";
import { GenerateMailingGroupsModal } from "./GenerateMailingGroupsModal";
import { publicationDisplayLabel } from "./mailingGroups.utils";
import styles from "./MailingGroups.module.css";

const FIRST_EDITION_YEAR = 2017;

type ApiError = Error & { status?: number; code?: string };

function isMixRegionError(error: unknown): boolean {
  return (error as ApiError | undefined)?.status === 400;
}

function isDuplicateNameError(error: unknown): boolean {
  const err = error as ApiError | undefined;
  return err?.status === 409 && err?.code === "GROUP_NAME_TAKEN";
}

type SortField = "edition" | "publicationName";
type SortDirection = "asc" | "desc";

function publicationLabel(item: MailingGroupsSummaryItem): string {
  const name = item.publicationName ?? `Publication ${item.publicationId ?? item.editionId}`;
  return publicationDisplayLabel(name, item.publicationEdition);
}

function MailingGroupsPage() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { updateUrl } = useUrlParams();
  const { toggleVisible, mode, taxonomy, ready } = useTaxonomyMode();
  const [editionFilter, setEditionFilter] = useState("");
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [sortField, setSortField] = useState<SortField>("publicationName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [{ value: "", label: "All Editions" }];
    for (let year = currentYear + 1; year >= FIRST_EDITION_YEAR; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const {
    data: summaryResponse,
    loading,
    error,
    refetch,
    isRefetching,
  } = useQuery(
    () =>
      ready
        ? mailingGroupsService.getSummary({ edition: editionFilter || undefined, taxonomy })
        : Promise.resolve(null),
    [ready, editionFilter, taxonomy],
  );

  const { mutate: createGroup } = useMutation((body: CreateMailingGroupBody) =>
    mailingGroupsService.create(body),
  );

  const items = useMemo(() => {
    const list = summaryResponse?.items ?? [];
    const sorted = [...list].sort((a, b) => {
      const left = sortField === "edition" ? a.edition : publicationLabel(a);
      const right = sortField === "edition" ? b.edition : publicationLabel(b);
      const cmp = left.localeCompare(right, undefined, { numeric: true });
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [summaryResponse?.items, sortField, sortDirection]);

  const handleSort = useCallback((field: string) => {
    const next = field as SortField;
    setSortField(prev => {
      if (prev === next) {
        setSortDirection(dir => (dir === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return next;
    });
  }, []);

  const handleGenerate = useCallback(
    async (body: CreateMailingGroupBody) => {
      try {
        await createGroup(body);
        const createdMode: TaxonomyMode = "editionId" in body ? "new" : "legacy";
        if (toggleVisible && createdMode !== mode) {
          updateUrl({ [TAXONOMY_URL_PARAM]: createdMode });
        } else {
          await refetch();
        }
        setToast({ message: "Mailing group generated successfully.", type: "success" });
      } catch (err) {
        if (isMixRegionError(err)) {
          setToast({
            message: "Cannot mix bar and standard regions in one mailing group.",
            type: "error",
          });
        } else if (isDuplicateNameError(err)) {
          setToast({
            message: "A group with this name already exists for this publication/edition.",
            type: "error",
          });
        } else {
          const message =
            err instanceof Error ? err.message : "Failed to generate mailing group. Please retry.";
          setToast({ message, type: "error" });
        }
        throw err;
      }
    },
    [createGroup, refetch, toggleVisible, mode, updateUrl],
  );

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    updateUrl({ [TAXONOMY_URL_PARAM]: next });
  };

  const columns: Column<MailingGroupsSummaryItem>[] = useMemo(
    () => [
      {
        header: "Edition",
        accessor: "edition" as keyof MailingGroupsSummaryItem,
      },
      {
        header: "Publication name",
        accessor: "publicationName" as keyof MailingGroupsSummaryItem,
        render: (_value: unknown, row: MailingGroupsSummaryItem) => publicationLabel(row),
      },
      {
        header: "Groups",
        accessor: "groupCount" as keyof MailingGroupsSummaryItem,
        render: (_value: unknown, row: MailingGroupsSummaryItem) => row.groupCount,
      },
      {
        header: "Campaigns",
        accessor: "campaignCount" as keyof MailingGroupsSummaryItem,
        render: (_value: unknown, row: MailingGroupsSummaryItem) => row.campaignCount,
      },
    ],
    [],
  );

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Referee Mailing Groups" subtitle="Survey mailing" />

      {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}

      <div className={styles.filterRow}>
        <div className={styles.filters}>
          <div className={styles.filterField}>
            <Select
              label="Edition"
              value={editionFilter}
              onChange={e => setEditionFilter(e.target.value)}
              options={editionOptions}
              size="small"
            />
          </div>
        </div>
      </div>

      <div className={styles.recipientsHeaderRow}>
        <h2 className={styles.sectionHeading}>Mailing groups</h2>
        <div className={styles.campaignsActions}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setIsGenerateModalOpen(true)}
            startIcon={<Icon icon="plus" size="small" />}
          >
            Generate mailing groups
          </Button>
        </div>
      </div>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {(loading || !ready) && summaryResponse === null ? (
        <LoadingState message="Loading mailing groups..." />
      ) : !error && items.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No mailing groups yet"
          message="No mailing groups have been generated. Use “Generate mailing groups” to create one."
        />
      ) : (
        !error && (
          <div className={styles.tableContainer}>
            <div className={styles.scopedTable}>
              <DataTable
                data={items}
                columns={columns}
                sortableColumnKeys={["edition", "publicationName"]}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                onRowClick={item =>
                  navigate(
                    ROUTES.surveys.campaignDetail(
                      String(item.publicationId ?? item.editionId),
                      item.edition,
                    ),
                  )
                }
                emptyMessage="No mailing groups found."
                rowKey="id"
              />
            </div>
          </div>
        )
      )}

      <GenerateMailingGroupsModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onSave={handleGenerate}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default MailingGroupsPage;
